import { NavLink } from 'react-router-dom'
import { cn } from '../lib/utils'
import logoLeao from '../assets/logo-leao.png'

interface AdminConfigSidebarProps {
  collapsed: boolean
  mobileOpen: boolean
  onCloseMobile: () => void
}

const ITENS = [
  { to: '/admin/config/planos', label: 'Planos', icon: 'ri-vip-crown-line' },
  { to: '/admin/config/recursos', label: 'Recursos por plano', icon: 'ri-toggle-line' },
  { to: '/admin/config/fipe', label: 'Monitor da FIPE', icon: 'ri-pulse-line' },
  { to: '/admin/config/pagamentos', label: 'Meios de pagamento', icon: 'ri-bank-card-line' },
  { to: '/admin/config/email', label: 'E-mail', icon: 'ri-mail-settings-line' },
  { to: '/admin/config/contatos', label: 'Contatos', icon: 'ri-customer-service-2-line' },
  { to: '/admin/config/faq', label: 'Dúvidas frequentes', icon: 'ri-question-line' },
  { to: '/admin/config/geral', label: 'Configurações gerais', icon: 'ri-settings-3-line' },
  { to: '/admin/config/importacao-pdf', label: 'Importação de PDF', icon: 'ri-file-pdf-2-line' },
  { to: '/admin/config/notificacoes', label: 'Notificações', icon: 'ri-notification-3-line' },
  { to: '/admin/config/pluggy', label: 'Pluggy', icon: 'ri-bank-line' },
  { to: '/admin/config/armazenamento', label: 'Armazenamento', icon: 'ri-database-2-line' },
]

/**
 * Menu à parte pra área de configuração da plataforma — separado do menu normal do app (Sidebar.tsx)
 * de propósito: essas telas não são sobre a conta financeira de ninguém, são sobre como o Walleon
 * funciona por baixo. O "Voltar" leva pro menu de sempre.
 */
export function AdminConfigSidebar({ collapsed, mobileOpen, onCloseMobile }: AdminConfigSidebarProps) {
  const compact = collapsed && !mobileOpen

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden animate-fade-in" onClick={onCloseMobile} aria-hidden />}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar border-r border-border/60 transition-[width,transform] duration-200 lg:static lg:z-auto lg:translate-x-0',
          compact ? 'w-[70px]' : 'w-[250px]',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Menu de configurações"
      >
        <div className={cn('flex h-[70px] flex-shrink-0 items-center gap-3', compact ? 'justify-center' : 'px-6')}>
          <img src={logoLeao} alt="" className="h-9 w-auto" />
          {!compact && <span className="text-base font-bold tracking-[0.2em] text-white">WALLEON</span>}
        </div>

        <div className={cn('pb-2', compact ? 'px-0' : 'px-3')}>
          <NavLink
            to="/dashboard"
            className={cn(
              'flex items-center rounded text-13 text-muted transition-colors hover:bg-surface2 hover:text-white',
              compact ? 'h-10 w-10 mx-auto justify-center' : 'gap-2 px-3 py-2',
            )}
            title={compact ? 'Voltar ao menu principal' : undefined}
          >
            <i className="ri-arrow-left-line text-lg" aria-hidden />
            {!compact && <span>Voltar ao menu principal</span>}
          </NavLink>
        </div>

        <nav className={cn('flex-1 pb-6', compact ? 'overflow-visible' : 'overflow-y-auto')}>
          {!compact && <p className="px-6 pb-2 pt-3 text-2xs font-semibold uppercase tracking-[0.05em] text-muted/70">Configurações</p>}
          <ul>
            {ITENS.map((item) => (
              <li key={item.to} className="group relative">
                <NavLink
                  to={item.to}
                  onClick={onCloseMobile}
                  className={({ isActive }) => cn(
                    'group/link flex w-full items-center text-15 transition-colors',
                    compact ? 'h-12 justify-center' : 'gap-2.5 px-6 py-2.5',
                    isActive ? 'text-white' : 'text-muted hover:text-white',
                  )}
                  title={compact ? item.label : undefined}
                >
                  {({ isActive }) => (
                    <>
                      <i className={cn(item.icon, 'text-lg leading-none', isActive ? 'text-primary' : 'group-hover/link:text-white')} aria-hidden />
                      {!compact && <span className="truncate">{item.label}</span>}
                    </>
                  )}
                </NavLink>
                {compact && (
                  <span className="pointer-events-none absolute left-full top-1/2 z-50 ml-1 hidden -translate-y-1/2 whitespace-nowrap rounded bg-surface2 px-3 py-1.5 text-13 text-white shadow-dropdown group-hover:block">
                    {item.label}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  )
}
